import { inject, Injectable } from '@angular/core';
import { IndexExportFormat } from '@libs/shared/domain/aggregations/index/index-export-format.schema';
import { OpenSearchRequest, OpenSearchRequestFilter } from '@libs/shared/domain/aggregations/opensearch/open-search-query.schema';
import { ROLE_FIELD_ADMIN, ROLE_PROGRAM_ADMIN } from '@libs/shared/domain/master-data/user/shared-master-data-user-role.type';
import { PROJECTION_INDEXES } from '@libs/shared/infrastructure/projection_indexes';
import { PharmacyViewModel } from '@services/models/pharmacy-view.model';
import { SettingsStore } from '@services/settings.store';
import { UserService } from '@services/user.service';
import { OpenSearchDomainService } from '@shared/models/open-search-domain-service';
import { FieldAdminsSettings } from '@shared/models/settings/field-admins-settings';
import { OpenSearchPage, OpenSearchQueryResult, OpenSearchSimpleSort } from '@shared/providers/opensearch-api';
import { OpenSearchClient } from '@shared/providers/opensearch-client';
import { OpenSearchService } from '@shared/providers/opensearch.service';
import { first } from 'lodash';
import { from, lastValueFrom, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class PharmaciesSearchService extends OpenSearchService implements OpenSearchDomainService<PharmacyViewModel> {
  readonly indexName = PROJECTION_INDEXES.PHARMACY_GENERAL_VIEW;
  readonly dataviewColumns = 'pharmacy_general_columns';

  private settingsStore = inject(SettingsStore);

  getByNcpdp(ncpdp: string | string[]): Observable<PharmacyViewModel | undefined> {
    return this.get<PharmacyViewModel>('ncpdp', ncpdp);
  }

  getByNcpdps(ncpdps: string[]): Observable<PharmacyViewModel[] | undefined> {
    return this.get<PharmacyViewModel[]>('ncpdp', ncpdps);
  }

  getByNpi(npi: string | string[]): Observable<PharmacyViewModel | undefined> {
    return this.get<PharmacyViewModel>('npi', npi);
  }

  getByNpis(npis: string[], fields?: string[]): Observable<PharmacyViewModel[] | undefined> {
    return this.get<PharmacyViewModel[]>('npi', npis, fields);
  }

  get<T>(searchBy: 'ncpdp' | 'npi', value: string | string[], fields?: string[]): Observable<T | undefined> {
    const filter: OpenSearchRequestFilter[] = [
      {
        field: searchBy,
        operator: Array.isArray(value) ? 'in' : 'equal',
        value: value,
      },
    ];
    const page: OpenSearchPage = { from: 0, size: Array.isArray(value) ? value.length : 1 };
    const sort: OpenSearchSimpleSort = { name: 'asc' };
    return this.searchData(filter, page, sort, fields).pipe(
      map(result => (Array.isArray(value) ? (result.items as T) : (result.items[0] as T))),
    );
  }

  countData(filter: OpenSearchRequestFilter[]): Observable<{ count: number }> {
    return from(this.updateFilterByRole(filter)).pipe(
      switchMap(updatedFilter => this.client.count(this.indexName, { filter: updatedFilter })),
      catchError(error => this.processCountError(error)),
    );
  }

  searchData<T = PharmacyViewModel>(
    filter: OpenSearchRequestFilter[],
    page: OpenSearchPage,
    sort: OpenSearchSimpleSort,
    fields?: string[],
  ): Observable<OpenSearchQueryResult<T>> {
    return from(this.updateFilterByRole(filter)).pipe(
      switchMap(updatedFilter => {
        const query: OpenSearchRequest = {
          filter: updatedFilter,
          page,
          sort: this.createSearchSort(sort),
          select: fields,
        };
        return this.client.search<T>(this.indexName, query);
      }),
      catchError(error => this.processSearchError<T>(error)),
    );
  }

  public exportData(
    filter: OpenSearchRequestFilter[],
    sort: OpenSearchSimpleSort,
    fields?: string[],
    format: IndexExportFormat = 'xlsx',
    dataviewColumns?: string,
    allColumns?: boolean,
  ): Observable<{
    fileUrl: string;
  }> {
    return from(this.updateFilterByRole(filter)).pipe(
      switchMap(updatedFilter =>
        this.client.export(
          this.indexName,
          { filter: updatedFilter, sort: this.createSearchSort(sort), select: fields },
          { dataview: this.dataviewColumns, format, ...OpenSearchClient.exportAllColumnsParam(allColumns) },
        ),
      ),
      catchError(error => {
        this.logger.error(error);
        throw error;
      }),
    );
  }

  public async validateUniqueNpi(npi: string): Promise<boolean> {
    const npiFilter: OpenSearchRequestFilter = { field: 'npi', operator: 'equal', value: npi };
    const find = await lastValueFrom(this.client.count(this.indexName, { filter: [npiFilter] }), { defaultValue: { count: 0 } });
    return find.count === 0;
  }

  public async getVisibilityProgramGroupsForFieldAdmin(userTenant: string): Promise<string[]> {
    const settings = await lastValueFrom(this.settingsStore.getSettings<FieldAdminsSettings>('field-admins', `tenant:${userTenant}`), {
      defaultValue: undefined,
    });
    return Object.entries(settings?.visibility.programGroup ?? {})
      .filter(([, isVisible]) => isVisible)
      .map(([programGroup]) => programGroup);
  }

  private async updateFilterByRole(filter: OpenSearchRequestFilter[]): Promise<OpenSearchRequestFilter[]> {
    const user = UserService.getCurrent();
    const userTenant = first(user.tenantIds) ?? 'NONE';
    const roleFilters: OpenSearchRequestFilter[] = [];

    if ([ROLE_PROGRAM_ADMIN].includes(user.role)) {
      roleFilters.push({
        field: 'enrollments',
        operator: 'in',
        value: [],
        nested: [
          {
            field: 'enrollments.tenantId',
            operator: 'in',
            value: user.tenantIds,
          },
          ...this.getEnrollmentConditions(),
        ],
      });
    }

    if (user.role === ROLE_FIELD_ADMIN) {
      const programGroups = await this.getVisibilityProgramGroupsForFieldAdmin(userTenant);
      roleFilters.push({
        field: 'enrollments',
        operator: 'in',
        value: [],
        nested: [
          {
            field: 'enrollments.tenantId',
            operator: 'in',
            value: user.tenantIds,
          },
          {
            field: 'enrollments.programGroup',
            operator: 'in',
            value: programGroups,
          },
          {
            field: 'enrollments.businessRegionId',
            operator: 'in',
            value: user.regions[userTenant] ?? ['NONE'],
          },
        ],
      });
    }

    return [...filter, ...roleFilters];
  }

  /**
   * Additional conditions for the enrollment matched by the role "enrollments" filter.
   */
  protected getEnrollmentConditions(): OpenSearchRequestFilter[] {
    return [];
  }
}
